import * as React from "react";
import { useIntl } from "react-intl";
import { Icon } from "semantic-ui-react";
import moment from "moment-timezone";
import { useSocialNetworks } from "../../../utils";

import * as DTO from "../../../interfaces";
import { SocialGrid } from "../../../styling/baseStyle";
import { SocialNetworkIconButton } from "../../SocialNetworkIconButton";

import { descriptors, PromptsType } from "./descriptors";

import { PromptCardStyle, PromptHeader, AutoPostLabel } from "./styled";

import "./styles.scss";
import { SimpleStepper } from "../../SimpleStepper";
import { useMemo } from "react";
import { MediaPlayer } from "../../MediaPlayer";
import { DateTimePicker } from "../../DateTimePicker";
import { TEXT_ONLY_SOCIAL_NETWORKS } from "../../../interfaces";

interface OwnProps {
  prompt: DTO.Prompt;
  campaignDeliveryMode: DTO.CampaignDeliveryMode;
  isAutoPost: boolean;
  isDragging?: boolean;
  deliveryDateUtc?: any;
  deliveryTimeUtc?: any;
  editable?: boolean;
  socialNetworks: string[];

  handleSetFieldValue: (field: string, value: any) => void;
}

type Props = OwnProps;

const PromptLargeCardFC: React.FC<Props> = ({
  prompt: { title, post, promptId },
  socialNetworks,
  campaignDeliveryMode,
  deliveryDateUtc,
  deliveryTimeUtc,
  isDragging = false,
  isAutoPost = false,
  editable = true,

  handleSetFieldValue,
}: Props) => {
  const [mediaIndex, setMediaIndex] = React.useState<number>(0);
  const { formatMessage } = useIntl();
  const { postableSocialNetworks, displayTikTok } = useSocialNetworks();
  const snKey = `${promptId}socialNetworks`;
  const timeKey = `${promptId}deliveryTimeUtc`;
  const dateKey = `${promptId}deliveryDateUtc`;
  const isOrderedRandom = campaignDeliveryMode === "ORDERED_RANDOM";
  const withPadding = isOrderedRandom && !isAutoPost;
  const scheduledCampaign = campaignDeliveryMode === "SPECIFIC_DATE";

  const media = useMemo(() => {
    const mediaList = post?.media?.sort((m1, m2) => m1.sortOrder - m2.sortOrder) || [];
    return mediaList[mediaIndex];
  }, [mediaIndex, post?.media]);

  const promptDate = useMemo(() => {
    return deliveryDateUtc ? moment(deliveryDateUtc).tz(moment.tz.guess()).toDate() : undefined;
  }, [deliveryDateUtc]);

  const promptTime = useMemo(() => {
    return deliveryTimeUtc ? moment(deliveryTimeUtc).tz(moment.tz.guess()).toDate() : undefined;
  }, [deliveryTimeUtc]);

  const autoPostSection = () => {
    return (
      <SocialGrid.Column width={displayTikTok ? 5 : 4} className={"leftBigPadding withPointerEvents"}>
        <AutoPostLabel>{formatMessage({ ...descriptors[PromptsType.autoPost] })}</AutoPostLabel>
        <SocialGrid leftpadding={"s"} className={"smallPaddingTop socialSection"}>
          {postableSocialNetworks.map((sn, index) => {
            const promptWithMedia = post?.media[0] && post?.media[0].uri;
            const verification = () => {
              return (!promptWithMedia && !TEXT_ONLY_SOCIAL_NETWORKS.includes(sn)) || !editable;
            };

            return (
              <SocialNetworkIconButton
                key={"prompt_card_" + index + sn}
                disabled={verification()}
                withPadding
                active={socialNetworks.includes(sn)}
                networkId={sn}
                onClickHandler={checked => {
                  let socialNetworksHash = {};
                  socialNetworks.map(
                    snId =>
                      (socialNetworksHash = {
                        ...socialNetworksHash,
                        [snId]: true,
                      })
                  );
                  const finalSn = {
                    ...socialNetworksHash,
                    [sn]: checked,
                  };
                  handleSetFieldValue(
                    snKey,
                    Object.keys(finalSn).filter(snId => finalSn[snId])
                  );
                }}
              />
            );
          })}
        </SocialGrid>
      </SocialGrid.Column>
    );
  };

  const adjustWidth = () => {
    if (isAutoPost && isOrderedRandom) {
      return 7;
    } else if (isAutoPost) {
      return 8;
    } else {
      return 11;
    }
  };

  return (
    <PromptCardStyle fluid className={isDragging ? "promptFluidCard dragging" : "promptFluidCard noBorderShadow"}>
      <PromptCardStyle.Content>
        <SocialGrid>
          <SocialGrid.Column width={3}>
            {media && (
              <SocialGrid.Row>
                <MediaPlayer media={media} />
              </SocialGrid.Row>
            )}
            {post?.media && post.media.length > 1 && (
              <SimpleStepper index={mediaIndex} onUpdate={setMediaIndex} total={post.media.length} />
            )}
          </SocialGrid.Column>
          <SocialGrid.Column width={adjustWidth()} className={withPadding ? "" : "noPaddingLateral"}>
            <PromptHeader>{title}</PromptHeader>
            {/* {!scheduledCampaign && <PromptMessage>{message}</PromptMessage>} */}
            {scheduledCampaign && (
              <DateTimePicker
                selectedDate={promptDate}
                selectedTime={promptTime}
                onChangeDate={date => handleSetFieldValue!(dateKey, date)}
                onChangeTime={time => handleSetFieldValue!(timeKey, time)}
              />
            )}
          </SocialGrid.Column>
          {isAutoPost && autoPostSection()}
          {isOrderedRandom && (
            <SocialGrid.Column width={1}>
              <Icon name={"align justify"} className={"sortIcon"} />
            </SocialGrid.Column>
          )}
        </SocialGrid>
      </PromptCardStyle.Content>
    </PromptCardStyle>
  );
};

export const PromptLargeCard = PromptLargeCardFC;
